import store from "@/store"
import { GraphicComponent, GraphicComponentSetting, IGraphicGDM, IGraphicsControllerStoreState, PlacementSetting, PlacementTypes } from "../graphics/types/Graphics.t"
import { ISwatch } from "@/store/modules/swatches/swatches.t"

export interface GraphicSwatchControllerProps {
    targetComponent: PlacementTypes
    swatch: ISwatch
    swatchGroup?: string
    swatchSubgroup: string,
    componentsWithGraphicsSupport?: PlacementTypes
}

/**
 * It creates a new component in the recipe if it doesn't exist yet.
 * @param {GraphicSwatchControllerProps} props - GraphicSwatchControllerProps
 */
const GraphicSwatchController = async (props: GraphicSwatchControllerProps) => {

    const targetComponent = props.targetComponent
    const swatch = props.swatch
    const swatchID = props.swatch.id
    const swatchGroup = props.swatchGroup ?? store.getters["swatches/returnSwatchSubgroupName"]({ swatch })
    const swatchSubgroup = props.swatchSubgroup ?? store.getters["swatches/returnSwatchSubgroupName"]({ swatch })

    const graphic: IGraphicGDM = await store.dispatch('gdm/GET_GDM', { id: swatchID, component3D: targetComponent })
    const componentsWithGraphicsSupport: PlacementTypes = props.componentsWithGraphicsSupport
        ?? store.getters["utils/componentsWithGraphicsSupport"]

    const recipe = (store.state as any).recipe.recipe_v2

    if (!recipe.components[targetComponent]) {
        const componentName = targetComponent
        if (!recipe.components[componentName]) {
            await store.commit("recipe/CREATE_COMPONENT", componentName)
            // checks if the component is the right sleeve and repeats the operation for the left sleeve
            if (componentName === "graphic_sleeve_right") {
                await store.commit("recipe/CREATE_COMPONENT", "graphic_sleeve_left")
            }
        }
        // updates the recipe when the component isn't yet in the recipe
        const placementSetting = ((store.state as any).graphicsController as IGraphicsControllerStoreState).placementSetting.find(item => item.placementComponent === componentName)
        const sub_components = graphic.component
            .map((item) => {
                const componentSetting = placementSetting?.componentSetting.find(c => c.id === item.id)
                let component: GraphicComponentSetting
                if (componentSetting) {
                    component = {
                        ...item,
                        hex: componentSetting.hex,
                        swatch_id: Number(componentSetting.swatch_id),
                        pattern: componentSetting.pattern,
                        has_ugc: componentSetting.has_ugc,
                        toolkit_options: componentSetting.toolkit_options,
                        ugc: componentSetting.ugc,
                    }
                } else {
                    component = { ...item }
                }
                // removes the ugc attribute that comes from the GMD when
                // subcomponent is not UGC
                if (!component.has_ugc) delete component.ugc
                return component
            })
            .map((item) => {
                const itemSwatch = store.getters['swatches/returnSwatchObj']({ swatchId: item.swatch_id })
                const itemGroup = itemSwatch ? store.getters["swatches/returnSwatchGroupName"]({ swatch: itemSwatch }) : "solids"
                const itemSubgroup = itemSwatch ? store.getters["swatches/returnSwatchSubgroupName"]({ swatch: itemSwatch }) : "solids-apparel"
                return {
                    [item.id]: {
                        swatch_id: item.swatch_id ?? 11021,
                        toolkit_options: item.toolkit_options ?? itemSwatch?.toolkit_options ?? {
                            hex: item.hex ?? itemSwatch.color,
                        },
                        group: item.has_ugc ? "ugc" : itemGroup,
                        material_type: itemSwatch?.material_type ?? "svg",
                        subgroup: item.has_ugc ? "ugc" : itemSubgroup,
                        ugc: item.ugc,
                    }
                }
            })
            .reduce(
                (item, acc) => Object.assign(item, acc),
                {}
            )

        await store.dispatch("recipe/UPDATE_RECIPE_COMPONENT", {
            componentName,
            swatchId: swatchID,
            group: swatchGroup,
            subgroup: swatchSubgroup,
            sub_components,
            material_type: componentsWithGraphicsSupport.includes(componentName) ? 'graphic' : undefined
        })

        // checks if the component is the right sleeve and repeats the operation for the left sleeve
        if (componentName === "graphic_sleeve_right") {
            await store.dispatch("recipe/UPDATE_RECIPE_COMPONENT", {
                componentName: "graphic_sleeve_left",
                swatchId: swatchID,
                group: swatchGroup,
                subgroup: swatchSubgroup,
                sub_components,
                material_type: componentsWithGraphicsSupport.includes("graphic_sleeve_left") ? 'graphic' : undefined
            })
        }
    }

    store.dispatch("standardNavigation/UPDATE_STANDARD_NAVIGATION", {
        activeComponent: targetComponent,
        activeSubComponent: "",
    })
}

export default GraphicSwatchController
